import { createListenerMiddleware } from '@reduxjs/toolkit';

import {
  selectAnchorSectionIds,
  selectAnchorSectionIsAnimating,
  selectAnchorSectionNavigationOptions,
} from '../selectors';
import * as slices from '../slices';
import { getStickyHeaderHeight as getGlobalStickyHeaderHeight } from '../../useHeaderHeight';

import type {
  AnyAction,
  ListenerEffect,
  PayloadAction,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import type { RootState } from '..';

const getScrollTop = () => document.scrollingElement?.scrollTop ?? 0;

// Use the global sticky header height function
const getStickyHeaderHeight = getGlobalStickyHeaderHeight;

const getOffset = (el: HTMLElement, additionalOffset = 0) => {
  const scrollTop = getScrollTop();
  const rect = el.getBoundingClientRect();

  // Get any custom scroll offset from data attributes
  const dataOffsetY = parseFloat(
    el.getAttribute('data-scroll-offset-y') || '0',
  );

  // Combine the data attribute offset with any additional offset
  const totalOffsetY = dataOffsetY + additionalOffset;

  return {
    top: Math.floor(rect.top + scrollTop) - totalOffsetY,
    bottom: Math.floor(rect.bottom + scrollTop) - totalOffsetY,
  };
};

let getState: () => RootState;

const setHash = (
  hash: string,
  { replace = true, state = window.history.state } = {},
) => {
  const url = new URL(window.location.href);
  url.hash = hash;
  window.history[replace ? 'replaceState' : 'pushState'](state, '', url);
  window.dispatchEvent(new PopStateEvent('popstate', { state }));
};

// Cache for section elements and offsets
const sectionCache = new Map<
  string,
  {
    section: HTMLElement;
    offset: { top: number; bottom: number };
    stickyHeaderHeight?: number;
  }
>();

const updateSectionCache = (sectionIds: string[]) => {
  sectionCache.clear();

  // Get the sticky header height once for all sections
  const stickyHeaderHeight = getStickyHeaderHeight();

  sectionIds.forEach((sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      // Get the offset, accounting for the sticky header height
      const offset = getOffset(section, stickyHeaderHeight);

      // Store the section with its offset and the sticky header height
      sectionCache.set(sectionId, {
        section,
        offset,
        stickyHeaderHeight,
      });
    }
  });
};

// Debounce timer for scroll events
let scrollDebounceTimer: number | null = null;

// Last time we updated the hash
let lastHashUpdateTime = 0;

// Last scroll position
let lastScrollTop = 0;

// Minimum time between hash updates (ms)
const MIN_HASH_UPDATE_INTERVAL = 200;

// Minimum visibility threshold for a section to be considered "in view" (percentage)
const MIN_VISIBILITY_THRESHOLD = 0.3; // 30% of the section must be visible

// Minimum time to wait before clearing the hash when no section is visible (ms)
const CLEAR_HASH_DELAY = 500; // Wait 500ms before clearing the hash

const handleScrolled = () => {
  const state = getState();
  const isAnimating = selectAnchorSectionIsAnimating(state);

  // Don't update hash during animations
  if (isAnimating) return;

  // Clear any existing debounce timer
  if (scrollDebounceTimer !== null) {
    window.clearTimeout(scrollDebounceTimer);
  }

  // Debounce scroll events to avoid excessive hash updates
  scrollDebounceTimer = window.setTimeout(() => {
    scrollDebounceTimer = null;

    // Check if enough time has passed since the last hash update
    const now = Date.now();
    if (now - lastHashUpdateTime < MIN_HASH_UPDATE_INTERVAL) return;

    const sectionIds = selectAnchorSectionIds(state);
    if (sectionCache.size !== sectionIds.length) {
      updateSectionCache(sectionIds);
    }

    const scrollTop = getScrollTop();
    let selectedSection = null;

    // Get the height of any sticky headers - use the cached value if available
    // or recalculate if needed
    let stickyHeaderHeight = 0;
    // If we have at least one section with a cached sticky header height, use that
    for (const [, { stickyHeaderHeight: cachedHeight }] of sectionCache) {
      if (cachedHeight !== undefined) {
        stickyHeaderHeight = cachedHeight;
        break;
      }
    }
    // If no cached value, calculate it now
    if (stickyHeaderHeight === 0) {
      stickyHeaderHeight = getStickyHeaderHeight();
    }

    // Adjust the effective scroll position to account for sticky headers
    const effectiveScrollTop = scrollTop + stickyHeaderHeight;

    // Determine scroll direction
    const scrollingDown = scrollTop > lastScrollTop;

    // Find the section that is currently in view
    // We prioritize sections based on scroll direction and visibility
    let minDistance = Infinity;
    const viewportTop = scrollTop;
    const viewportBottom = viewportTop + window.innerHeight;

    for (const [, { section, offset }] of sectionCache) {
      // Calculate how much of the section is visible in the viewport
      const sectionHeight = offset.bottom - offset.top;
      const visibleTop = Math.max(offset.top, viewportTop);
      const visibleBottom = Math.min(offset.bottom, viewportBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / sectionHeight;

      // A section is considered "in view" if it meets the following criteria:
      // 1. It must be at least partially visible in the viewport
      const isPartiallyVisible =
        (offset.top >= viewportTop && offset.top <= viewportBottom) || // Top edge in viewport
        (offset.bottom >= viewportTop && offset.bottom <= viewportBottom) || // Bottom edge in viewport
        (offset.top <= viewportTop && offset.bottom >= viewportBottom); // Section spans viewport

      // 2. It must meet one of these conditions:
      //    a. When scrolling down: The section's top is visible and at least MIN_VISIBILITY_THRESHOLD of it is in view
      //    b. When scrolling up: The section's bottom is visible and at least MIN_VISIBILITY_THRESHOLD of it is in view
      //    c. The section contains the effective scroll position (traditional method)
      const isInViewByPosition =
        offset.top <= effectiveScrollTop && offset.bottom > effectiveScrollTop;
      const isInViewByVisibility = visibilityRatio >= MIN_VISIBILITY_THRESHOLD;
      const isTopVisible =
        offset.top >= viewportTop && offset.top <= viewportBottom;
      const isBottomVisible =
        offset.bottom >= viewportTop && offset.bottom <= viewportBottom;

      // Only consider a section in view if it's partially visible AND meets the direction-specific criteria
      const isInView =
        isPartiallyVisible &&
        (isInViewByPosition ||
          (scrollingDown && isTopVisible && isInViewByVisibility) ||
          (!scrollingDown && isBottomVisible && isInViewByVisibility));

      if (isInView) {
        // Calculate distance based on scroll direction
        let distance;
        if (scrollingDown) {
          // When scrolling down, prioritize sections whose top is closer to the viewport top
          distance = Math.abs(offset.top - viewportTop);
        } else {
          // When scrolling up, prioritize sections whose bottom is closer to the viewport bottom
          distance = Math.abs(offset.bottom - viewportBottom);
        }

        // Select the section with the smallest distance
        if (distance < minDistance) {
          minDistance = distance;
          selectedSection = section;
        }
      }
    }

    // If no section was found using the strict criteria, check if any section is visible in the viewport
    if (selectedSection === null) {
      // We already have viewportTop and viewportBottom from above
      let anySectionVisible = false;

      // Special case: Check if we're at the very top of the page
      // Use a more generous threshold to ensure we catch it
      const isAtPageTop = scrollTop < 50; // Within 50px of the top

      // Special case: Check if we're at the very bottom of the page
      // Use a more generous threshold to ensure we catch it
      const documentHeight = document.documentElement.scrollHeight;
      const isAtPageBottom =
        scrollTop + window.innerHeight >= documentHeight - 50; // Within 50px of the bottom

      // If we're at the top of the page, select the first section if it's close to the top
      if (isAtPageTop && sectionCache.size > 0) {
        // Find the topmost section
        let topmostSection = null;
        let topmostOffset = Infinity;

        for (const [, { section, offset }] of sectionCache) {
          if (offset.top < topmostOffset) {
            topmostOffset = offset.top;
            topmostSection = section;
          }
        }

        // Always select the topmost section when at the top of the page
        // This ensures we always have a section selected at the top
        selectedSection = topmostSection;
        anySectionVisible = true;
      }
      // If we're at the bottom of the page, select the last section if it's close to the bottom
      else if (isAtPageBottom && sectionCache.size > 0) {
        // Find the bottommost section
        let bottommostSection = null;
        let bottommostOffset = -Infinity;

        for (const [, { section, offset }] of sectionCache) {
          if (offset.bottom > bottommostOffset) {
            bottommostOffset = offset.bottom;
            bottommostSection = section;
          }
        }

        // Always select the bottommost section when at the bottom of the page
        // This ensures we always have a section selected at the bottom
        selectedSection = bottommostSection;
        anySectionVisible = true;
      }

      // If we haven't selected a section based on page position, check visibility
      if (!anySectionVisible) {
        // Check if any section is actually visible in the viewport
        for (const [, { section, offset }] of sectionCache) {
          // Calculate how much of the section is visible in the viewport
          const sectionHeight = offset.bottom - offset.top;
          const visibleTop = Math.max(offset.top, viewportTop);
          const visibleBottom = Math.min(offset.bottom, viewportBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibilityRatio = visibleHeight / sectionHeight;

          // Check if any part of the section is visible
          const isPartiallyVisible =
            (offset.top >= viewportTop && offset.top <= viewportBottom) || // Top edge in viewport
            (offset.bottom >= viewportTop && offset.bottom <= viewportBottom) || // Bottom edge in viewport
            (offset.top <= viewportTop && offset.bottom >= viewportBottom); // Section spans viewport

          // Don't consider a section visible if it's not at least partially visible
          if (!isPartiallyVisible) {
            continue;
          }

          // A section is considered visible if:
          // 1. When scrolling down: The section's top is visible and at least MIN_VISIBILITY_THRESHOLD/2 of it is in view
          // 2. When scrolling up: The section's bottom is visible and at least MIN_VISIBILITY_THRESHOLD/2 of it is in view
          // 3. Or if enough of the section is visible regardless of scroll direction
          const isTopVisible =
            offset.top >= viewportTop && offset.top <= viewportBottom;
          const isBottomVisible =
            offset.bottom >= viewportTop && offset.bottom <= viewportBottom;

          // Use a slightly higher threshold for the fallback check to be more strict
          const fallbackThreshold = MIN_VISIBILITY_THRESHOLD * 0.75; // 75% of the main threshold

          // Determine if the section is visible enough based on scroll direction
          const isVisible =
            visibilityRatio >= fallbackThreshold ||
            (scrollingDown &&
              isTopVisible &&
              visibilityRatio >= fallbackThreshold) ||
            (!scrollingDown &&
              isBottomVisible &&
              visibilityRatio >= fallbackThreshold);

          if (isVisible) {
            anySectionVisible = true;

            // Calculate distance based on scroll direction
            let distance;
            if (scrollingDown) {
              // When scrolling down, prioritize sections whose top is closer to the viewport top
              distance = Math.abs(offset.top - viewportTop);
            } else {
              // When scrolling up, prioritize sections whose bottom is closer to the viewport bottom
              distance = Math.abs(offset.bottom - viewportBottom);
            }

            if (distance < minDistance) {
              minDistance = distance;
              selectedSection = section;
            }
          }
        }
      }

      // If no section is visible in the viewport, don't select any section
      // This will cause the fragment to be reset
      if (!anySectionVisible) {
        selectedSection = null;
      }
    }

    const hash = window.location.hash.replace(/^#/, '');

    // Check if the user is actively scrolling
    const isActivelyScrolling = Math.abs(scrollTop - lastScrollTop) > 50;
    lastScrollTop = scrollTop;

    // Check if any section is actually visible in the viewport
    let anyVisibleSection = false;
    if (selectedSection !== null) {
      // Double-check that the selected section is actually visible
      const section = selectedSection;
      const sectionData = Array.from(sectionCache.values()).find(
        (data) => data.section === section,
      );

      if (sectionData) {
        const offset = sectionData.offset;
        const sectionHeight = offset.bottom - offset.top;
        const visibleTop = Math.max(offset.top, viewportTop);
        const visibleBottom = Math.min(offset.bottom, viewportBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibilityRatio = visibleHeight / sectionHeight;

        // A section is considered visible if it's at least partially in the viewport
        const isPartiallyVisible =
          (offset.top >= viewportTop && offset.top <= viewportBottom) || // Top edge in viewport
          (offset.bottom >= viewportTop && offset.bottom <= viewportBottom) || // Bottom edge in viewport
          (offset.top <= viewportTop && offset.bottom >= viewportBottom); // Section spans viewport

        // Only consider it visible if it meets the minimum visibility threshold
        anyVisibleSection =
          isPartiallyVisible &&
          visibilityRatio >= MIN_VISIBILITY_THRESHOLD * 0.5;
      }
    }

    if (selectedSection === null || !anyVisibleSection) {
      // Only clear the hash if:
      // 1. There is a hash to clear
      // 2. The user is not actively scrolling (to prevent flickering during fast scrolling)
      // 3. Enough time has passed since the last hash update
      if (
        hash &&
        !isActivelyScrolling &&
        Date.now() - lastHashUpdateTime > CLEAR_HASH_DELAY
      ) {
        setHash('');
        lastHashUpdateTime = Date.now();
      }
    } else {
      const selectedHash = `#${selectedSection.id}`;

      // Only update if the hash has changed
      if (hash !== selectedSection.id) {
        const selectedOptions = selectAnchorSectionNavigationOptions(
          state,
          selectedSection.id,
        );
        setHash(selectedHash, selectedOptions);
        lastHashUpdateTime = Date.now();
      }
    }
  }, 50); // 50ms debounce
};

const handleAnchorSectionIdsModified: ListenerEffect<
  PayloadAction<unknown>,
  RootState,
  ThunkDispatch<RootState, unknown, AnyAction>,
  unknown
> = (_action, api) => {
  getState = api.getState;

  const prevSectionIds = selectAnchorSectionIds(api.getOriginalState());
  const sectionIds = selectAnchorSectionIds(api.getState());

  if (sectionIds.length !== 0 && prevSectionIds.length === 0) {
    window.addEventListener('scroll', handleScrolled, {
      passive: true,
    });
    window.addEventListener('scrollend', handleScrolled, {
      passive: true,
    });
  } else if (sectionIds.length === 0 && prevSectionIds.length !== 0) {
    window.removeEventListener('scroll', handleScrolled);
    window.removeEventListener('scrollend', handleScrolled);
  }
};

const anchorSectionsMiddleware = createListenerMiddleware();

anchorSectionsMiddleware.startListening({
  actionCreator: slices.anchorSections.actions.sectionAdd,
  effect: (action, api) => handleAnchorSectionIdsModified(action, api as never),
});

anchorSectionsMiddleware.startListening({
  actionCreator: slices.anchorSections.actions.sectionRemove,
  effect: (action, api) => handleAnchorSectionIdsModified(action, api as never),
});

export const anchorSections = anchorSectionsMiddleware.middleware;
